import { FC } from 'react';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@components/atoms/Button';
import { useHistory } from 'react-router-dom';
import { Loading } from '@components/atoms/Loading';
import AvatarSection from '@pages/settings/components/AvatarSection';
import ProfileForm from '@pages/settings/components/ProfileForm';
import PasswordForm from '@pages/settings/components/PasswordForm';
import {
  useCurrentUser,
  useGetVersion,
  useUpdateMinVersion,
  useUpdateCurrentUser,
  useUpdatePassword,
  useUpdateAvatar,
  useUserOrganizations,
  USER_ORGANIZATIONS_QUERY_KEY,
} from '@hooks/api';
import { OrganizationCard } from '@components/organisms/organization/OrganizationCard';
import CreateOrganizationModal from '@components/organisms/CreateOrganizationModal';
import { LanguageSwitcher } from '@components/molecules/LanguageSwitcher';
import { DailySiteReportSettingsCard } from '@components/organisms/DailySiteReport/DailySiteReportSettingsCard';
import { useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { UserRoles } from '@models/user.model';
import { isExternalViewerRole } from '@constants/roleHierarchy';
import { useTranslation } from '@hooks/useTranslation';
import { buildPasswordSchema } from '@utils/passwordPolicy';

const ProfilePage: FC = () => {
  const tSettings = useTranslation('settings');
  const tMenu = useTranslation('menu');
  const tPolicy = useTranslation('passwordPolicy');
  const history = useHistory();
  const queryClient = useQueryClient();
  const [avatar, setAvatar] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isCreateOrgModalOpen, setIsCreateOrgModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { rememberMe, role } = useAppSelector((state: RootState) => state.user);
  // Sprint 25, story-127 sweep item #12, corrected by D5 for web: name/avatar stay editable
  // (updateOwnUser is on the external-viewer write allowlist) — only createOrganization, which
  // is not on that allowlist and would fail server-side, needs hiding here.
  const isExternalViewer = isExternalViewerRole(role);

  const { isPending: isLoadingMinVersion } = useUpdateMinVersion();
  const { data: currentUser, isLoading: isLoadingCurrentUser } =
    useCurrentUser();
  const { isLoading: isLoadingVersion } = useGetVersion();
  const {
    mutateAsync: updateCurrentUserMutation,
    isPending: isLoadingUpdateCurrentUser,
  } = useUpdateCurrentUser();
  const { mutateAsync: updatePassword, isPending: isUpdatingPassword } =
    useUpdatePassword();
  const { mutateAsync: updateAvatar, isPending: isUpdatingAvatar } =
    useUpdateAvatar();
  const { data: userOrganizations, isLoading: isLoadingOrganizations } =
    useUserOrganizations();

  // Get current organization from Redux
  const currentOrganizationId = useAppSelector(
    (state: RootState) => state.organization.organizationId,
  );

  const isSuperAdmin = currentUser?.getSelf?.role === UserRoles.SUPER_ADMIN;
  // Sort organizations to show active one first
  const sortedOrganizations = React.useMemo(() => {
    if (!userOrganizations) return [];
    return [...userOrganizations].sort((a, b) => {
      const aIsActive = a.organizationId === currentOrganizationId;
      const bIsActive = b.organizationId === currentOrganizationId;
      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;
      return 0;
    });
  }, [userOrganizations, currentOrganizationId]);

  const isLoading =
    isLoadingCurrentUser ||
    isLoadingMinVersion ||
    isLoadingVersion ||
    isLoadingUpdateCurrentUser ||
    isUpdatingPassword ||
    isUpdatingAvatar ||
    isLoadingOrganizations;

  const profileFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: currentUser?.getSelf?.firstName || '',
      lastName: currentUser?.getSelf?.lastName || '',
    },
    validationSchema: Yup.object({
      firstName: Yup.string().trim().required('First name is required'),
      lastName: Yup.string().trim().required('Last name is required'),
    }),
    onSubmit: async (values) => {
      await updateCurrentUserMutation({
        firstName: values.firstName,
        lastName: values.lastName,
        userId: currentUser?.getSelf?.userId,
      });
    },
  });

  const passwordFormik = useFormik({
    enableReinitialize: false,
    initialValues: { oldPassword: '', newPassword: '' },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required('Old password is required'),
      // Sprint 47, story-233: the shared rule (was `.{6,}`, unified to 8).
      newPassword: buildPasswordSchema(tPolicy),
    }),
    onSubmit: async (values, { resetForm }) => {
      await updatePassword({
        previousPassword: values.oldPassword,
        newPassword: values.newPassword,
        rememberMe,
      });
      resetForm();
    },
  });


  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      setAvatar(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogout = () => {
    // Navigate through the dedicated /logout route so PrivateRoute's
    // `pathname !== '/logout'` guard prevents the current path (/profile)
    // from being re-saved as redirectUrl during the logout render cycle.
    history.push('/logout');
  };

  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleOrganizationCreated = (organizationId: string) => {
    // Invalidate user organizations query to refresh the list
    queryClient.invalidateQueries({ queryKey: USER_ORGANIZATIONS_QUERY_KEY });
    setIsCreateOrgModalOpen(false);
  };

  useEffect(() => {
    if (avatar && imagePreview) {
      updateAvatar(avatar);
    }
  }, [avatar, imagePreview, updateAvatar]);

  if (isLoading) {
    return <Loading center size="2xl" />;
  }

  return (
    <div className="h-screen">
      <div className="flex items-center gap-2">
        <AccountCircleOutlinedIcon
          sx={{ fontSize: 28 }}
          className="text-orange-400 dark:text-orange-300"
        />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {tSettings.profileTitle}
        </h1>
      </div>
      <AvatarSection
        fileInputRef={fileInputRef}
        onOpenFileDialog={openFileDialog}
        imageSrc={imagePreview || currentUser?.getSelf?.avatar || ''}
        onFileChange={handleImageChange}
        displayText={
          currentUser?.getSelf?.firstName ||
          currentUser?.getSelf?.lastName ||
          currentUser?.getSelf?.email
        }
      />
      <ProfileForm
        email={currentUser?.getSelf?.email || ''}
        saving={isLoadingUpdateCurrentUser}
        isSubmitting={profileFormik.isSubmitting}
        handleBlur={profileFormik.handleBlur}
        handleSubmit={profileFormik.handleSubmit}
        handleChange={profileFormik.handleChange}
        values={{
          firstName: profileFormik.values.firstName,
          lastName: profileFormik.values.lastName,
        }}
        touched={{
          firstName: profileFormik.touched.firstName,
          lastName: profileFormik.touched.lastName,
        }}
        errors={{
          firstName: profileFormik.errors.firstName as string,
          lastName: profileFormik.errors.lastName as string,
        }}
      />

      <LanguageSwitcher userId={currentUser?.getSelf?.userId} />

      <DailySiteReportSettingsCard />

      {currentUser?.getSelf?.loginMethod === 'cognito' && (
        <PasswordForm
          handleBlur={passwordFormik.handleBlur}
          isSubmitting={passwordFormik.isSubmitting}
          handleSubmit={passwordFormik.handleSubmit}
          handleChange={passwordFormik.handleChange}
          values={{
            oldPassword: passwordFormik.values.oldPassword,
            newPassword: passwordFormik.values.newPassword,
          }}
          touched={{
            oldPassword: passwordFormik.touched.oldPassword,
            newPassword: passwordFormik.touched.newPassword,
          }}
          errors={{
            oldPassword: passwordFormik.errors.oldPassword as string,
            newPassword: passwordFormik.errors.newPassword as string,
          }}
        />
      )}

      {/* Organizations Section */}
      <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-900 rounded-xl">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {tSettings.myOrganizations}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {tSettings.myOrganizationsHint}
            </p>
          </div>
          {!isSuperAdmin && !isExternalViewer && (
            <button type="button"
              onClick={() => setIsCreateOrgModalOpen(true)}
              className="px-4 py-2.5 bg-indigo-900 dark:bg-indigo-700 text-white rounded-lg hover:bg-indigo-800 dark:hover:bg-indigo-600 transition flex items-center gap-2 shadow-sm">
              <Building2 size={18} />
              {tSettings.createOrganization}
            </button>
          )}
        </div>
        {sortedOrganizations && sortedOrganizations.length > 0 ? (
          <div className="grid gap-4">
            {sortedOrganizations.map((userOrg: any) => (
              <OrganizationCard
                key={userOrg.organizationId}
                userOrganization={userOrg}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <Building2
              size={48}
              className="mx-auto text-gray-400 dark:text-gray-500 mb-4"
            />
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {tSettings.noOrganizationsYet}
            </p>
            {!isSuperAdmin && !isExternalViewer && (
              <button type="button"
                onClick={() => setIsCreateOrgModalOpen(true)}
                className="px-4 py-2 bg-indigo-900 dark:bg-indigo-700 text-white rounded-lg hover:bg-indigo-800 dark:hover:bg-indigo-600 transition">
                {tSettings.createFirstOrganization}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col w-[200px] gap-4 mt-12 pb-12">
        <Button variant="primary" onClick={handleLogout}>
          {tMenu.logout}
        </Button>
      </div>

      <CreateOrganizationModal
        isOpen={isCreateOrgModalOpen}
        onClose={() => setIsCreateOrgModalOpen(false)}
        onSuccess={handleOrganizationCreated}
        userData={{
          firstName: currentUser?.getSelf?.firstName,
          lastName: currentUser?.getSelf?.lastName,
          email: currentUser?.getSelf?.email,
        }}
      />
    </div>
  );
};

export default ProfilePage;
